import withInputFields from "../../helpers/inputfields";
import withReactiveFields from "../../helpers/reactivefields";
import { USES_CHARGE_POLLING } from "../../../../charge/flags";
import { getValueAtPath, withProperties, isBoolean } from "../../../../utils";
import getChargeLimit from "../../../../lookup/limit";
import getChargeFee from "../../../../lookup/fee";
import ConfirmingView from "../../common/confirming";
import PendingView from "../../common/pending";
import ErrorState from "../../../common/error";

import {
  selectCountryFromList,
  selectNetworkFromList,
  fetchFrancophoneNetworks
} from "../../../../collections/mobilemoneynetworks";

import QRView from "./views/code";
import InitView from "./views/init";
import ValidateView from "./views/otp";
import ConfirmView from "./views/confirm";
import SuccessView from "../../common/success";
import RedirectView from "../../common/redirect";
import withCountdownTimer from "../../helpers/countdowntimer";

const METHOD = 'mobilemoneyfranco';
const METHOD_NAME = 'Mobile Money';
const CHARGE_MODE = USES_CHARGE_POLLING;

const VIEWS = [ InitView, ValidateView, ConfirmView, QRView ];

function mount() {
  return (dispatch) => {
    // Some logic here to determine the view to show instead of
    // always showing the form (first) view, nd also other stuffs
    // like fee check, limit check, etc.
    getChargeLimit.call(this, () => {
      const VIEW = (
        this.QR_CODE && (QRView || (this.resetContext()))
      ) || (
        this.REDIRECT_URL && (RedirectView || this.$resetContext())
      ) || InitView;

      VIEW && VIEW.activate(() => {
        fetchFrancophoneNetworks(this)
          .then(() => {
            // First set country to null before refreshing to ensure
            // that all change subscription functions are fired.
            this.$inputs.$update('country', null);
            this.$inputs.$reset('country');
          });
      });
    });

    getChargeFee();
  };
}

function getChargeData() {
  if (this.SHOULD_VALIDATE_CHARGE) {
    return { momo_sn: true }
  }

  return ({
    is_mobile_money_franco: true,
    payment_type: 'mobilemoneyfranco'
  });
}

function getViewsContext(data, store) {
  let initialCountries = getValueAtPath(data, 'mobilemoneyfranco_countries');
  const TIMER_DURATION = 5 * 60;
  const currency = getValueAtPath(data, 'currency');
  let COUNTRIES = initialCountries;
  if (['XAF', 'XOF'].indexOf(currency) >= 0) {
    COUNTRIES = initialCountries.filter(country => country.currency === currency);
  }
  const REACTIVE_FIELDS = ['NETWORKS', 'CONFIRM_INSTRUCTION', 'REDIRECT_URL', 'SHOULD_VALIDATE_CHARGE', 'CURRENT_TIME', 'QR_CODE'];
  const $context = withReactiveFields(Object.create(null), ...REACTIVE_FIELDS);

  $context.pollConfig = {
    onBegin() { $context.$timer && $context.$timer.start() },
    onCancel() { $context.pollConfig.onComplete() },
    onSuccessful() { SuccessView && SuccessView.activate.call($context) },
    onError() { $context.CONFIRM_INSTRUCTION = null, $context.$timer && $context.$timer.clear() },
    onComplete() { $context.$timer && $context.$timer.clear() /* self.console.log({ ...$context }) */ }
  }

  $context.$subscribe('REDIRECT_URL', (currentValue, oldValue) => {
    setTimeout(() => {
      if (!(oldValue && oldValue === currentValue) && currentValue instanceof URL) {
        RedirectView && RedirectView.activate.call($context);
      }
    }, 0);
  });

  $context.$subscribe('SHOULD_VALIDATE_CHARGE', (currentValue, oldValue) => {
    setTimeout(() => {
      if (oldValue !== currentValue && isBoolean(currentValue)) {
        if (currentValue === true) {
          ValidateView && ValidateView.activate();
        } else {
          if ($context.QR_CODE) {            
            QRView && QRView.activate(() => {
              scheduleQRAutoPoll();
            });
          } else if ($context.REDIRECT_URL) {
            return;
          } else {
            ConfirmView && ConfirmView.activate(() => {
              $context.$charge.poll($context.pollConfig);
            });
          }
        }
      }
    }, 0);
  });

  $context.$subscribe('NETWORKS', (currentValue, oldValue) => {
    setTimeout(() => {
      if (oldValue !== currentValue) {
        $context.$inputs.$refresh('network');
        $context.$inputs.$reset('network');

        // Hack for updating/autofilling network input state.
        // if (InitView && InitView.ACTIVE) {
        //   const update = getValueAtPath(InitView.STATE, '$actions.update');
        //   callback(update)({ network: getValueAtPath($context.$inputs, '[network].value') || null });
        // }
      }
    }, 0);
  });

  withInputFields($context, {
    phone: getValueAtPath(data, 'phone') || null,
    country: {
      options: COUNTRIES,
      get selector() { return selectCountryFromList.bind($context, this.options) },
      defaultValue: getValueAtPath(data, 'country') || null
    },
    network: {
      get options() { return $context.NETWORKS },
      get selector() { return selectNetworkFromList.bind($context, this.options) },
      defaultValue: getValueAtPath(data, 'mobilemoney_network') || null,
      available: hasNetworks,
      required: hasNetworks
    },
    authorization_code: getValueAtPath(data, 'authorization_code') || null,
  });

  $context.$inputs.$subscribe('country', (currentValue, oldValue) => {
    setTimeout(() => {
      if (oldValue !== currentValue) {
        const country = getValueAtPath(currentValue, '[value].code') || null;
        $context.NETWORKS = country && getValueAtPath(data, `mobilemoney_networks[${country}]`);

        setTimeout(() => {
          if (country && getValueAtPath(data, `mobilemoney_networks[${country}]`) === undefined) {
            fetchFrancophoneNetworks($context);
          }
        }, 3500)
      }
    }, 0);
  });

  $context.$inputs.$subscribe('network', (currentValue, oldValue) => {
    setTimeout(() => {
      const { currency, method  } = $context.STATE;
      if (oldValue !== currentValue && (currency.current === 'XOF' || currency.current === 'XAF') && method.current === 'mobilemoneyfranco') {        
        setTimeout(() => {
          getChargeFee();
        }, 0);
      }
    }, 0);
  });

  $context.$subscribe('QR_CODE', (currentValue, oldValue) => {
    setTimeout(() => {
      if (!oldValue && currentValue && oldValue !== currentValue) {
        QRView && QRView.activate(() => {
          scheduleQRAutoPoll();
        });
      }
    }, 0);
  });

  // Auto-start polling on the QR view (Senegal/SN and other XOF/XAF QR-based flows)
  function scheduleQRAutoPoll() {
    setTimeout(() => {
      const hasStartedPolling = getValueAtPath($context, 'STATE.backgroundProcessing');
      const currentPaymentMethod = getValueAtPath($context, 'STATE.method.current');
      const currentView = getValueAtPath($context, 'STATE.view.current');
      if (
        hasStartedPolling === false &&
        currentPaymentMethod === 'mobilemoneyfranco' &&
        currentView === 'MOMO_CODE_VIEW' &&
        $context.QR_CODE
      ) {
        $context.$charge.poll($context.pollConfig);
      }
    }, 10000);
  }

  withProperties($context, {
    TIMER_DURATION,
    $resetContext() {
      $context.CONFIRM_INSTRUCTION = $context.REDIRECT_URL = $context.SHOULD_VALIDATE_CHARGE = $context.QR_CODE = null;
    },
    INIT_VIEW: callback => {
      const responseCode = getValueAtPath($context, '$$data.data.response_code');
      const hasError = !!getValueAtPath($context, '$$data.data.$error') === true || getValueAtPath($context, '$$data.status') === 'failed';

      if (!["00", "02"].includes(responseCode)) {
        $context.$resetContext();
        InitView && InitView.activate(callback);
      } else if (responseCode === "02" && !hasError) {
        setTimeout(() => {
          ConfirmingView && ConfirmingView.activate(() => {
            $context.$charge.poll($context.pollConfig);
          });
        }, 0)
      }
    }
  });

  function hasNetworks() {
    return getValueAtPath($context.NETWORKS, 'length') > 0;
  }

  return withCountdownTimer($context, {
    duration: TIMER_DURATION,
    onTick() { $context.CURRENT_TIME = $context.$timer.current },
    onFinish() {
      if ($context.$charge.cancel()) {
        $context.$timer && $context.$timer.clear();
        $context.CURRENT_TIME = null;
        $context.CONFIRM_INSTRUCTION = null;
        $context.QR_CODE = null;
        $context.REDIRECT_URL = null;
        setTimeout(() => {
          ErrorState.actions.updateErrorMessage('');
          PendingView && PendingView.activate.call($context)
        }, 10)
      }
    }
  })
}

export {
  METHOD,
  METHOD_NAME,
  CHARGE_MODE,
  VIEWS,
  mount,
  getChargeData,
  getViewsContext
}
